'use client';

import { useEffect, useState } from 'react';

/**
 * Effect-based `window.location` subscription (patches pushState/replaceState
 * and listens for popstate). Use this instead of `usePathname()`/
 * `useSearchParams()` when the URL only drives EFFECTS, not render output:
 * unlike the Next hooks it is not URL data, so components using it stay legal
 * in prerendered shells and Suspense fallbacks on routes without statically
 * known params (e.g. the [...catchAll] 404 route). Returns empty strings on
 * the server and until the mount effect fires.
 *
 * Extracted from product-main.tsx; shared by the PDP and the site header.
 */
export function useBrowserLocation() {
	const [location, setLocation] = useState({ pathname: '', search: '' });

	useEffect(() => {
		const updateLocation = () => {
			setLocation((currentLocation) => {
				const nextLocation = {
					pathname: window.location.pathname,
					search: window.location.search,
				};

				if (
					currentLocation.pathname === nextLocation.pathname &&
					currentLocation.search === nextLocation.search
				) {
					return currentLocation;
				}

				return nextLocation;
			});
		};

		updateLocation();
		return subscribeToLocationChanges(updateLocation);
	}, []);

	return location;
}

const locationListeners = new Set<() => void>();
let historyPatched = false;

/**
 * History is patched ONCE per page and never restored. Per-instance patching
 * (each hook saving and restoring `history.pushState`) breaks when instances
 * unmount out of order: child effects mount before parent effects, so a PDP's
 * hook patched first and the layout's (analytics page_view) wrapped it; when
 * the PDP unmounted it restored the ORIGINAL pushState, silently unhooking
 * the layout for the rest of the session.
 */
export function subscribeToLocationChanges(listener: () => void) {
	locationListeners.add(listener);
	if (!historyPatched) {
		historyPatched = true;
		const notify = () => {
			for (const notifyListener of locationListeners) notifyListener();
		};
		const scheduleNotify = () => queueMicrotask(notify);
		const originalPushState = window.history.pushState;
		const originalReplaceState = window.history.replaceState;

		window.history.pushState = function pushState(...args) {
			originalPushState.apply(window.history, args);
			scheduleNotify();
		};

		window.history.replaceState = function replaceState(...args) {
			originalReplaceState.apply(window.history, args);
			scheduleNotify();
		};

		window.addEventListener('popstate', notify);
	}

	return () => {
		locationListeners.delete(listener);
	};
}
